export type AutosaveOptions = {
  testId: string;
  data: {
    title: string;
    collection_id?: number;
    difficulty?: string;
    description?: string;
    time_limit_seconds?: number;
    shuffle_answers?: boolean;
    questions_attributes: Array<{
      uuid: string;
      content?: string;
      position: number;
      question_type: string;
      image?: string;
      answer_options_attributes: Array<{
        uuid: string;
        content?: string;
        is_correct: boolean;
        _destroy?: boolean;
      }>;
      _destroy?: boolean;
    }>;
  };
  onSuccess?: (serverUpdatedAt: number) => void;
  onError?: (error: string | number) => void;
};

export class AutosaveService {
  private timeoutId: ReturnType<typeof setTimeout> | null = null;
  private isSaving = false;
  private pendingOptions: AutosaveOptions | null = null;
  private lastSavedAt: number = 0;
  private readonly debounceMs: number;

  constructor(debounceMs: number = 5000) {
    this.debounceMs = debounceMs;
  }

  scheduleSave(options: AutosaveOptions): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    this.timeoutId = setTimeout(async () => {
      await this.save(options);
    }, this.debounceMs);
  }

  private async save(options: AutosaveOptions): Promise<void> {
    if (this.isSaving) {
      this.pendingOptions = options;
      return;
    }
    this.isSaving = true;
    this.pendingOptions = null;

    const csrfToken = document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content");

    try {
      const response = await fetch(`/studio/tests/${options.testId}`, {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
        body: JSON.stringify({ test: options.data }),
      });

      if (response.ok) {
        const result = await response.json();
        this.lastSavedAt = Date.now();
        options.onSuccess?.(result.updated_at);
      } else {
        options.onError?.(response.status);
      }
    } catch {
      options.onError?.("network");
    } finally {
      this.isSaving = false;
      if (this.pendingOptions) {
        const nextOptions = this.pendingOptions;
        this.pendingOptions = null;
        await this.save(nextOptions);
      }
    }
  }

  async saveNow(options: AutosaveOptions): Promise<void> {
    this.cancel();
    await this.save(options);
  }

  cancel(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    this.pendingOptions = null;
  }

  getLastSavedAt(): number {
    return this.lastSavedAt;
  }
}
